<template>
	<v-chart
		ref="chart"
		class="chart"
		:option="option"
		:init-options="{ renderer: 'svg' }"
		@rendered="onChartRendered"
		autoresize
	/>
</template>




<script>
import { watchEffect } from 'vue'
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
// https://echarts.apache.org/handbook/en/concepts/dataset/#
import VChart from 'vue-echarts'
import * as echarts from 'echarts'
// import { lightningSchema, useLightnings } from '@/composables/lightnings.js'
import { useEvents, eventSchema } from '@/composables/events.js'
import {
	chartDateStr,
	firstYearDay,
	lastYearDay,
	// dayStart,
	dayEnd,
} from '@/helpers.js'

import { use } from 'echarts/core'
import {
	HeatmapChart
} from 'echarts/charts'
import {
	TitleComponent,
	TooltipComponent,
	VisualMapComponent,
	CalendarComponent
} from 'echarts/components'
import {
	CanvasRenderer
} from 'echarts/renderers'

use([
	TitleComponent,
	TooltipComponent,
	VisualMapComponent,
	CalendarComponent,
	HeatmapChart,
	CanvasRenderer
])




export default {
	components: {
		VChart,
	},


	props: {
		stationId: {
			type: String,
			require: true,
		},
		// Year for chow in heatmap
		// For update current year just togle year as Number and as String
		year: {
			type: [Number, String],
			require: true,
		},
	},


	emits: {
		selectedDay: value => value instanceof Date && !Number.isNaN(value.getTime()),
		isLoading:  value => typeof value === "boolean",
	},


	setup() {
		const {
			allEvents: lightningEvents,
			isLoading,
			error,
			setFilters,
		} = useEvents()

		const option = {
			title: {
				top: 0,
				left: 'center',
				text: 'Блискавки навколо СКЗ'
			},

			tooltip: {
				// Tooltip builder function
				formatter: (params/*, ticket, callback*/) => {
					const date = new Date(params.value[0])
					const count = Number(params.value[1])

					let tooltip = `<div class="text-center">`
						// Addtooltip header
						tooltip += `${chartDateStr(date)}`
						if (count > 0) {
							tooltip += ` <b>${count} шт</b>`
						}
					tooltip += `</div>`

					return tooltip
				},
			},

			visualMap: {
				min: 0,
				max: 10,
				type: 'piecewise',
				dimension: 1, // get value from data[1]
				orient: 'horizontal',
				left: 'center',
				top: 40,
				seriesIndex: 0,
				selectedMode: false,
				pieces: [
					{ value: 0, color: '#e5e8fb' },
					{ min: 0, max: 2, color: '#bac0f5' },
					{ min: 2, max: 4, color: '#8f99f0' },
					{ min: 4, max: 6, color: '#6471ea' },
					{ min: 6, max: 8, color: '#1b2ed0' },
					{ min: 8, max: 10, color: '#101b7a' },
					{ min: 10, color: '#0a114f' },
				],
			},

			// Calendar configuration
			calendar: {
				top: 100,
				left: 40,
				right: 15,
				cellSize: ['auto', 20],
				range: String((new Date()).getFullYear()),
				itemStyle: {
					borderWidth: 1,
				},
				yearLabel: {
					show: false
				},
				monthLabel: {
					nameMap: ['Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер', 'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру'],
				},
				dayLabel: {
					nameMap: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
					firstDay: 1 // start on Monday
				},
			},

			// Data series
			series: [
				{
					type: 'heatmap',
					coordinateSystem: 'calendar',
					selectedMode: 'single',
					data: [],
				},
			]
		}
		const heatmapSerieIndex = 0

		return {
			lightningEvents,
			isLoading,
			error,
			setFilters,
			
			option,
			heatmapSerieIndex,
		}
	},


	data() {
		return {
		}
	},

	watch: {
		// Update chart on year update
		async year() {
			this.updateChartYear()
		},

		async stationLightningsInTimePeriod() {
			this.$nextTick(() => {
				this.updateChartSeriesData()
			})
		},

		isLoading(value) {
			// console.log('loading', value)
			this.$emit('isLoading', value)

			if (value) {
				// Show loading animation
				this.chartInstance()?.showLoading('default', {
					text: '',
					color: '#5470C6',
					textColor: '#000',
					maskColor: 'rgba(255, 255, 255, 0.8)',
					zlevel: 0,
				})
			} else {
				// Hide loading animation
				this.chartInstance()?.hideLoading()
			}
		}
	},

	created() {},

	mounted() {
		// this.updateChartYear()
		this.setClickOnDayHandler()
		// Set current day as selected to trigger chart update
		this.onClickDay(new Date())
	},

	methods: {
		chartInstance() {
			return this.$refs.chart?.chart
		},

		onChartRendered() {
			// Read lightning events
			watchEffect(() => {
				this.setFilters({
					fromTime: firstYearDay(this.$props.year),
					toTime: lastYearDay(this.$props.year),
					eventTypes: [eventSchema.eventType.allowed.lightning.key, ],
					stationIds: [this.$props.stationId, ],
				})
			})
		},

		updateChartYear() {
			this.chartInstance()?.setOption({
				calendar: {
					range: this.year,
				},
			})
		},

		updateChartSeriesData() {
			if (this.chartInstance() == undefined 
				|| this.chartInstance().getOption()?.series == undefined
			) {
				return
			}

			// Get empty year
			const heatmapData = this.fillAllDaysByDefault()
			// Increment lightning counters
			this.stationLightningsInTimePeriod.forEach(lightning => {
				const key = this.dateToKey(lightning[eventSchema.payload.allowed.flashTimeUTC.key])
				heatmapData[key][1]++
			})

			// Apply new data to chart
			this.chartInstance().setOption({
				series:
				[
					{
						data: Object.values(heatmapData),
					},
				],
			})
		},

		fillAllDaysByDefault() {
			const days = {}

			for (var d = new Date(this.startDate); d <= this.endDate; d.setDate(d.getDate() + 1)) {
				const key = this.dateToKey(d)
				// Init day data bay zero lightnings
				days[key] = [key, 0]
			}

			return days
		},

		dateToKey(date) {
			return echarts.time.format(date, '{yyyy}-{MM}-{dd}', false)
		},

		setClickOnDayHandler() {
			this.chartInstance()?.off('click')

			// Set handler for click on day
			this.chartInstance()?.on('click', (params) => {
				this.onClickDay(new Date(params.value[0]))

				this.chartInstance()?.dispatchAction({
					type: 'select',
					seriesIndex: params.seriesIndex,
					dataIndex: params.dataIndex,
				});
			})
		},

		onClickDay(date) {
			// console.log('Click on day', date)
			this.$emit('selectedDay', date)
		},
	},


	computed: {
		stationLightningsInTimePeriod() {
			return this.lightningEvents.map((event) => event[eventSchema.payload.key])
		},

		startDate() {
			return firstYearDay(this.year)
		},

		endDate() {
			// If filling current year
			if (this.year == new Date().getFullYear()) {
				return dayEnd(new Date())
			} else {
				return lastYearDay(this.year)
			}
		},	
	},
}
</script>




<style scoped>
</style>